import { type Static, Type } from "@sinclair/typebox"
import { IdSchema, IsoTimestampSchema, SeatIdSchema } from "./common"
import { SkillModeSchema } from "./skills"

export const ActionKindSchema = Type.Union([
  Type.Literal("play_card"),
  Type.Literal("attack"),
  Type.Literal("use_ability"),
  Type.Literal("swap_active_unit"),
  Type.Literal("recruit_card"),
  Type.Literal("level_up_hero"),
  Type.Literal("end_turn"),
  Type.Literal("concede"),
])

export type ActionKind = Static<typeof ActionKindSchema>

export const ActionCommandMetaSchema = Type.Object({
  commandId: IdSchema,
  matchId: IdSchema,
  seatId: SeatIdSchema,
  turnNumber: Type.Integer({
    minimum: 1,
  }),
  clientContext: Type.Optional(
    Type.Object({
      localActionIndex: Type.Optional(
        Type.Integer({
          minimum: 0,
        }),
      ),
      submittedAt: Type.Optional(IsoTimestampSchema),
    }),
  ),
})

export const PlayCardCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("play_card"),
    payload: Type.Object({
      instanceId: IdSchema,
      destination: Type.Union([
        Type.Literal("active"),
        Type.Literal("support"),
      ]),
      replaceSupportId: Type.Optional(IdSchema),
    }),
  }),
])

export type PlayCardCommand = Static<typeof PlayCardCommandSchema>

export const AttackCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("attack"),
    payload: Type.Object({
      attackerId: IdSchema,
      target: Type.Object({
        type: Type.Union([
          Type.Literal("central_command"),
          Type.Literal("unit"),
        ]),
        seatId: SeatIdSchema,
        instanceId: Type.Optional(IdSchema),
      }),
    }),
  }),
])

export type AttackCommand = Static<typeof AttackCommandSchema>

export const UseAbilityCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("use_ability"),
    payload: Type.Object({
      sourceId: IdSchema,
      skillId: Type.String({
        minLength: 1,
      }),
      mode: Type.Optional(SkillModeSchema),
      allyRequirement: Type.Optional(
        Type.Union([Type.Literal(1), Type.Literal(2)]),
      ),
      target: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    }),
  }),
])

export type UseAbilityCommand = Static<typeof UseAbilityCommandSchema>

export const SwapActiveUnitCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("swap_active_unit"),
    payload: Type.Object({
      fromSupportId: IdSchema,
    }),
  }),
])

export type SwapActiveUnitCommand = Static<typeof SwapActiveUnitCommandSchema>

export const RecruitCardCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("recruit_card"),
    payload: Type.Object({
      source: Type.Union([
        Type.Literal("merc_row"),
        Type.Literal("better_battle_bot_lane"),
      ]),
      slotId: Type.Optional(IdSchema),
    }),
  }),
])

export type RecruitCardCommand = Static<typeof RecruitCardCommandSchema>

export const LevelUpHeroCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("level_up_hero"),
    payload: Type.Object({
      targetLevel: Type.Union([Type.Literal(2), Type.Literal(3)]),
    }),
  }),
])

export type LevelUpHeroCommand = Static<typeof LevelUpHeroCommandSchema>

export const EndTurnCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("end_turn"),
    payload: Type.Object({}),
  }),
])

export type EndTurnCommand = Static<typeof EndTurnCommandSchema>

export const ConcedeCommandSchema = Type.Intersect([
  ActionCommandMetaSchema,
  Type.Object({
    kind: Type.Literal("concede"),
    payload: Type.Object({}),
  }),
])

export type ConcedeCommand = Static<typeof ConcedeCommandSchema>

export const ActionCommandSchema = Type.Union([
  PlayCardCommandSchema,
  AttackCommandSchema,
  UseAbilityCommandSchema,
  SwapActiveUnitCommandSchema,
  RecruitCardCommandSchema,
  LevelUpHeroCommandSchema,
  EndTurnCommandSchema,
  ConcedeCommandSchema,
])

export type ActionCommand = Static<typeof ActionCommandSchema>
